/**
 * Actions - Owned (On-page) Page (Spec 045, 066, 067)
 *
 * Shows all owned/on-page actions across all playbooks for the selected brand.
 * Groups by content type (066) and shows inspiration URLs (067).
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router';
import { useBrand } from '../lib/brand-context.js';
import { apiGet, apiFetch } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon, Spinner } from '../components/ui/Icon.js';
import { Select } from '../components/Select.js';
import { useFullBleed } from '../lib/layout-width.js';

interface InspirationUrl {
  url: string;
  title?: string;
  domain?: string;
  retrieval_rate?: number;
}

interface ActionItem {
  id: string;
  action_type: string;
  title: string;
  description: string | null;
  target_urls: string[] | null;
  effort_estimate: string | null;
  status: string;
  completed_at: string | null;
  notes: string | null;
  playbook_id: string;
  playbook_title: string;
  content_type?: string | null;
  inspiration_urls?: InspirationUrl[] | null;
}

interface ActionsResponse {
  data: ActionItem[];
  total: number;
}

const STATUS_COLORS: Record<string, string> = {
  todo: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  done: 'bg-green-100 text-green-700',
  skipped: 'bg-yellow-100 text-yellow-700',
};

const CONTENT_TYPE_LABELS: Record<string, string> = {
  article: 'Article',
  how_to_guide: 'How-to guide',
  product_page: 'Product page',
  category_page: 'Category page',
  other: 'Other',
};

const ACTION_STATUSES = ['todo', 'in_progress', 'done', 'skipped'] as const;

export function ActionsOwnedPage() {
  useFullBleed();
  const { selectedBrand, user } = useBrand();
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [contentTypeFilter, setContentTypeFilter] = useState('');

  const fetchActions = useCallback(async () => {
    if (!selectedBrand || !user) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ category: 'owned', limit: '100' });
      if (statusFilter) params.set('status', statusFilter);
      const result = await apiGet<ActionsResponse>(
        `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/actions?${params.toString()}`
      );
      setActions(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load actions');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, user, statusFilter]);

  useEffect(() => { fetchActions(); }, [fetchActions]);

  const filteredActions = useMemo(() => {
    if (!contentTypeFilter) return actions;
    return actions.filter((a) => (a.content_type ?? 'other') === contentTypeFilter);
  }, [actions, contentTypeFilter]);

  // Count by content type for sidebar
  const contentTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of actions) {
      const ct = a.content_type ?? 'other';
      counts[ct] = (counts[ct] ?? 0) + 1;
    }
    return counts;
  }, [actions]);

  const updateStatus = async (playbookId: string, actionId: string, status: string) => {
    try {
      await apiFetch(`/api/v1/playbooks/${playbookId}/actions/${actionId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      fetchActions();
    } catch { /* ignore */ }
  };

  if (!selectedBrand) {
    return (
      <div className="hub-page--table" data-density="compact">
        <h1 className="hub-page__title">Owned actions</h1>
        <EmptyState icon="bx-book-open" title="Select a brand" description="Choose a brand to view owned actions." />
      </div>
    );
  }

  const doneCount = filteredActions.filter((a) => a.status === 'done').length;
  const skippedCount = filteredActions.filter((a) => a.status === 'skipped').length;
  const todoCount = filteredActions.filter((a) => a.status === 'todo').length;

  return (
    <div data-testid="actions-owned-page" className="hub-page--table" data-density="compact">
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title">Owned actions (on-page)</h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">All on-page/technical actions across playbooks for {selectedBrand.name}</p>
        </div>
      </div>

      <div className="hub-toolbar">
        <Select
          options={[
            { value: '', label: 'All statuses' },
            ...ACTION_STATUSES.map((s) => ({ value: s, label: s.replace('_', ' ') })),
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="All statuses"
          className="w-44"
        />
        <Select
          options={[
            { value: '', label: 'All content types' },
            ...Object.entries(CONTENT_TYPE_LABELS).map(([v, l]) => ({ value: v, label: `${l} (${contentTypeCounts[v] ?? 0})` })),
          ]}
          value={contentTypeFilter}
          onChange={setContentTypeFilter}
          placeholder="All content types"
          className="w-56"
        />
      </div>

      {/* Summary counts (066) */}
      {!loading && actions.length > 0 && (
        <div className="flex gap-4 text-sm" data-testid="action-counts">
          <span className="text-[var(--hub-color-ink-muted)]">All: <strong>{filteredActions.length}</strong></span>
          <span className="text-[var(--hub-color-success)]">Done: <strong>{doneCount}</strong></span>
          <span className="text-[var(--hub-color-warning)]">Skipped: <strong>{skippedCount}</strong></span>
          <span className="text-[var(--hub-color-ink-subtle)]">Todo: <strong>{todoCount}</strong></span>
        </div>
      )}

      {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {loading && <div className="flex justify-center py-8"><Spinner className="text-[var(--hub-color-action-text)]" /></div>}

      {!loading && actions.length === 0 && (
        <EmptyState
          icon="bx-book-open"
          title="No owned actions yet"
          description="Generate a playbook to create actionable items."
          variant="onboarding"
          actionLabel="Go to playbooks"
          actionHref="/playbooks"
        />
      )}

      {!loading && filteredActions.length > 0 && (
        <div className="overflow-x-auto">
          <table className="hub-table" data-testid="owned-actions-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>Type</th>
                <th>Content</th>
                <th>Playbook</th>
                <th>Effort</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredActions.map((action) => (
                <tr key={action.id}>
                  <td>
                    <div className="font-medium">{action.title}</div>
                    {action.description && <div className="mt-0.5 text-xs text-[var(--hub-color-ink-muted)] line-clamp-1">{action.description}</div>}
                    {/* Inspiration URLs (067) */}
                    {action.inspiration_urls && action.inspiration_urls.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {action.inspiration_urls.slice(0, 3).map((insp, idx) => (
                          <a
                            key={idx}
                            href={insp.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-[var(--hub-color-action-text)]"
                            data-testid="inspiration-url"
                          >
                            <Icon name="bx-link-external" />
                            Take inspiration from {insp.title ?? insp.domain ?? insp.url.slice(0, 40)}
                          </a>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="hub-chip hub-chip--info">{action.action_type}</span>
                  </td>
                  <td>
                    <span className="hub-chip">
                      {CONTENT_TYPE_LABELS[action.content_type ?? 'other'] ?? 'Other'}
                    </span>
                  </td>
                  <td>
                    <Link to={`/playbooks/${action.playbook_id}`} className="text-[var(--hub-color-action-text)]">{action.playbook_title}</Link>
                  </td>
                  <td>{action.effort_estimate ?? '--'}</td>
                  <td>
                    <Select
                      options={ACTION_STATUSES.map((s) => ({ value: s, label: s.replace('_', ' ') }))}
                      value={action.status}
                      onChange={(v) => updateStatus(action.playbook_id, action.id, v)}
                      className="w-32"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
